import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Coins, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useKickletPoints } from "@/hooks/useKickletPoints";
import { useDailyResetCountdown } from "@/hooks/useDailyResetCountdown";
import { NotificationBell } from "@/components/NotificationBell";
import logo from "@/assets/logo.svg";
import textLogo from "@/assets/logo.png";
import dailyGift from "@/assets/daily-gift.svg";
import { tierOf, type TierKey } from "@/config/tiers";

interface HeaderProps {
  showSidebarTrigger?: boolean;
}

export function Header({ showSidebarTrigger = false }: HeaderProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [userPoints, setUserPoints] = useState<number | null>(null);
  const [canClaimDaily, setCanClaimDaily] = useState(false);
  const { getPoints, loading: pointsLoading } = useKickletPoints();
  const timeRemaining = useDailyResetCountdown();
  const [userProfile, setUserProfile] = useState<{
    kick_name: string | null;
    tier: TierKey | null;
    onboarding_completed: boolean | null;
  } | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setUserProfile(null);
        setUserPoints(null);
        setCanClaimDaily(false);
        return;
      }

      // Fetch profile and daily login status in parallel
      const [profileResult, dailyLoginResult] = await Promise.all([
      supabase.
      from("profiles").
      select("kick_name, tier, onboarding_completed").
      eq("id", user.id).
      maybeSingle(),
      supabase.
      from("daily_logins").
      select("last_login_date").
      eq("user_id", user.id).
      maybeSingle()]
      );

      if (profileResult.data) {
        setUserProfile(profileResult.data);
        if (profileResult.data.kick_name) {
          const pointsData = await getPoints(profileResult.data.kick_name);
          if (pointsData) {
            setUserPoints(pointsData.points);
          }
        }
      }

      // Check if user can claim daily login
      const today = new Date().toISOString().split('T')[0];
      const hasClaimedToday = dailyLoginResult.data?.last_login_date === today;
      setCanClaimDaily(!hasClaimedToday);
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  return (
    <header className="relative z-50 h-[72px] border-b border-border/50 bg-background">
      <div className="flex h-full items-center justify-center px-4">
        {/* Mobile: Text logo centered (visible only on very small screens) */}
        <div className="sm:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <img
            src={textLogo}
            alt="GamDucks"
            className="h-8 w-auto cursor-pointer"
            onClick={() => navigate("/")} />

        </div>

        {/* Left: Mobile menu trigger + HypeDrop Timer (hidden on mobile, shown on sm+) */}
        <div className="hidden sm:flex items-center gap-3 flex-1">
          {showSidebarTrigger && <SidebarTrigger className="md:hidden" />}
          
          {/* Daily Login Timer - only show when bonus is available */}
          {user && canClaimDaily && userProfile?.onboarding_completed &&
          <button
            type="button"
            onClick={() => navigate("/daily-login")}
            aria-label="Claim your daily bonus"
            className="daily-bonus relative ml-3.5 hidden sm:flex items-center h-10 pl-[48px] pr-4 rounded-[10px] border-2 border-primary bg-gradient-to-b from-[hsl(178_70%_16%)] to-[hsl(190_60%_10%)] transition-transform hover:-translate-y-px active:translate-y-px">

              <img src={dailyGift} alt="" className="daily-bonus-gift absolute -left-3.5 top-1/2 -mt-[30px] h-[54px] w-[54px] drop-shadow-[0_3px_3px_hsl(195_60%_3%/0.7)]" />
              <span className="flex flex-col leading-none font-heading italic -skew-x-6 text-left">
                <span className="text-[13px] font-bold text-white">Daily</span>
                <span className="text-base font-black tracking-wide text-primary [text-shadow:0_0_8px_hsl(var(--primary)/0.5)]">BONUS</span>
              </span>
              <span className="daily-bonus-badge absolute -top-2 -right-2 min-w-5 h-5 rounded-full bg-[hsl(45_100%_58%)] text-background border-2 border-background font-heading font-black text-xs leading-4 text-center">1</span>
              <span className="absolute -bottom-[7px] right-3.5 px-1.5 rounded-full bg-background border border-primary/50 text-[9px] leading-3 font-medium text-primary whitespace-nowrap tabular-nums">
                {[timeRemaining.hours, timeRemaining.minutes, timeRemaining.seconds].map((n) => String(n).padStart(2, '0')).join(':')}
              </span>
            </button>
          }
        </div>

        {/* Center: Logo - hidden on mobile (shown in sidebar instead) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden sm:block">
          <img

            alt="GamDucks"
            className="h-16 md:h-20 w-auto cursor-pointer"
            onClick={() => navigate("/")} src="/lovable-uploads/e8b26943-3f5e-44e8-bfaf-65ab2b1d70c2.png" />

        </div>

        {/* Right: User info or Sign in (hidden on mobile, shown on sm+) */}
        <div className="hidden sm:flex ml-auto items-center gap-3">
          {user && userProfile ?
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card/50 border border-border/50">
              <div className="flex items-center gap-1.5 text-primary">
                <Coins className="h-4 w-4" />
                {pointsLoading ?
              <Loader2 className="h-4 w-4 animate-spin" /> :

              <span className="font-semibold text-sm">{userPoints?.toLocaleString() ?? '—'}</span>
              }
              </div>
              <div className="w-px h-4 bg-border/50" />
              {tierOf(userProfile.tier).image ? (
                <img
                  src={tierOf(userProfile.tier).image!}
                  alt={tierOf(userProfile.tier).label}
                  title={tierOf(userProfile.tier).label}
                  className="h-6 w-6 object-contain"
                />
              ) : (
                <span className="text-xs font-medium capitalize text-muted-foreground">unranked</span>
              )}
              <div className="w-px h-4 bg-border/50" />
              <NotificationBell />
              <div className="hidden sm:block w-px h-4 bg-border/50" />
              <span
              onClick={() => navigate("/profile")}
              className="hidden sm:block text-sm font-medium text-foreground truncate max-w-[120px] cursor-pointer hover:text-primary transition-colors">

                {userProfile.kick_name || "No username"}
              </span>
            </div> :

          <div className="flex items-center gap-2">
              <Button
              onClick={() => navigate("/auth?tab=signup")}
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10">

                Register
              </Button>
              <Button
              onClick={() => navigate("/auth")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground">

                Sign In
              </Button>
            </div>
          }
        </div>
      </div>
    </header>);

}

export default Header;